import { getToken, clearAuth } from '../auth'

// Falls back to localhost so a fresh clone works even though .env is gitignored.
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000'
export const USE_MOCKS = process.env.REACT_APP_USE_MOCKS === 'true'

export class ApiError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Thin fetch wrapper: injects the JWT, parses JSON, and normalises errors.
 * On 401 it clears the session and bounces to /login (expired/invalid token).
 * 403 is surfaced to the caller so pages can show "insufficient permissions".
 */
export async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (auth && token) headers.Authorization = `Bearer ${token}`

  let res
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch (networkErr) {
    throw new ApiError('Cannot reach the server. Is the backend running?', 0)
  }

  // A 401 on a request that never carried a token (e.g. login itself) can't be
  // an expired session — surface the backend's actual message instead.
  if (res.status === 401 && auth) {
    clearAuth()
    if (window.location.pathname !== '/login') {
      window.location.assign('/login')
    }
    throw new ApiError('Session expired. Please sign in again.', 401)
  }

  let data = null
  const text = await res.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!res.ok) {
    const message = (data && data.message) || `Request failed (${res.status})`
    throw new ApiError(message, res.status)
  }

  return data
}
